import { useState } from 'react'
import api from '../utils/api'

interface RegisterFormProps {
  onSuccess: () => void
}

interface FormData {
  email: string
  prenom: string
  password: string
  confirmPassword: string
  telephone: string
}

interface FormErrors {
  email?: string
  prenom?: string
  password?: string
  confirmPassword?: string
  telephone?: string
  general?: string
}

export default function RegisterForm({ onSuccess }: RegisterFormProps) {
  const [formData, setFormData] = useState<FormData>({
    email: '',
    prenom: '',
    password: '',
    confirmPassword: '',
    telephone: ''
  })
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<FormErrors>({})

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est obligatoire'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Format email invalide'
    }

    if (!formData.prenom.trim()) {
      newErrors.prenom = 'Le prénom est obligatoire'
    }

    if (!formData.password) {
      newErrors.password = 'Le mot de passe est obligatoire'
    } else if (formData.password.length < 6) {
      newErrors.password = 'Minimum 6 caractères'
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Confirmez votre mot de passe'
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas'
    }

    if (!formData.telephone.trim()) {
      newErrors.telephone = 'Le téléphone est obligatoire'
    } else if (!/^[0-9+\-\s()]+$/.test(formData.telephone)) {
      newErrors.telephone = 'Format téléphone invalide'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    setErrors(prev => ({
      ...prev,
      [name]: undefined
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    try {
      setLoading(true)
      setErrors({})

      const response = await api.post('/api/register', {
        email: formData.email.toLowerCase(),
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        prenom: formData.prenom,
        telephone: formData.telephone
      })

      // Stocker les données pour la redirection
      localStorage.setItem('userEmail', formData.email.toLowerCase())
      localStorage.setItem('userId', response.data.userId)

      onSuccess()
      
      // Rediriger vers ProfilePage pour compléter le profil
      // Use window.location for reliable navigation in tabbed context
      setTimeout(() => {
        window.location.href = '/profile'
      }, 100)
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Erreur lors de l\'inscription'
      setErrors({ general: errorMessage })
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="auth-form">
      {errors.general && (
        <div className="error-message">
          <span className="error-icon">!</span>
          {errors.general}
        </div>
      )}

      <div className="form-group">
        <label htmlFor="register-email">Email professionnel</label>
        <input
          id="register-email"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="votre.email@entreprise.com"
          disabled={loading}
        />
        {errors.email && (
          <div className="error-message">
            <span className="error-icon">!</span>
            {errors.email}
          </div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="register-prenom">Prénom</label>
        <input
          id="register-prenom"
          type="text"
          name="prenom"
          value={formData.prenom}
          onChange={handleChange}
          placeholder="Votre prénom"
          disabled={loading}
        />
        {errors.prenom && (
          <div className="error-message">
            <span className="error-icon">!</span>
            {errors.prenom}
          </div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="register-phone">Téléphone</label>
        <input
          id="register-phone"
          type="tel"
          name="telephone"
          value={formData.telephone}
          onChange={handleChange}
          placeholder="+33 6 12 34 56 78"
          disabled={loading}
        />
        {errors.telephone && (
          <div className="error-message">
            <span className="error-icon">!</span>
            {errors.telephone}
          </div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="register-password">Mot de passe</label>
        <input
          id="register-password"
          type="password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          placeholder="Minimum 6 caractères"
          disabled={loading}
        />
        {errors.password && (
          <div className="error-message">
            <span className="error-icon">!</span>
            {errors.password}
          </div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="register-confirm">Confirmer le mot de passe</label>
        <input
          id="register-confirm"
          type="password"
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleChange}
          placeholder="Confirmez votre mot de passe"
          disabled={loading}
        />
        {errors.confirmPassword && (
          <div className="error-message">
            <span className="error-icon">!</span>
            {errors.confirmPassword}
          </div>
        )}
      </div>

      <button
        type="submit"
        className="btn-primary"
        disabled={loading}
      >
        {loading ? (
          <>
            <span className="spinner"></span>
            Inscription en cours...
          </>
        ) : (
          'Créer un compte'
        )}
      </button>
    </form>
  )
}
